/** Reviewer comment box for a contract stage tab.
 *
 *  Always visible under the agent's rendered output. Lets the reviewer
 *  attach a free-text note to the stage WITHOUT approving it. The note
 *  persists into agent_results.edits_json.reviewer_note and surfaces
 *  on the dashboard hero card as a purple ring around the stage's dot.
 *
 *  Empty string + Save clears the existing note.
 */
import { useEffect, useState } from 'react';
import { Loader2, MessageSquare, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { api, ApiError } from '@/lib/api';
import { gateForStage } from '@/pages/jobs/stages/gateGroups';

interface Props {
  jobId: string;
  stage: string;
  /** Existing note text (so reviewer can edit, not just replace). */
  initialNote: string | null;
  /** Fired after a successful save so the parent can re-fetch agents
   *  and the dashboard ring picks up the change. */
  onSaved: () => void;
}

export function StageCommentBox({ jobId, stage, initialNote, onSaved }: Props) {
  const [note, setNote] = useState(initialNote ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    setNote(initialNote ?? '');
    setSavedAt(null);
    setError(null);
  }, [jobId, stage, initialNote]);

  const dirty = note !== (initialNote ?? '');
  const gate = gateForStage(stage);
  const gateLabel = gate === 'gate_1' ? 'Gate 1' : gate === 'gate_2' ? 'Gate 2' : null;

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/jobs/${jobId}/stages/${stage}/comment`, {
        note,
      });
      setSavedAt(new Date());
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save comment.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-4 rounded-md border border-violet-200 bg-violet-50/40 dark:border-violet-900 dark:bg-violet-950/20 px-3 py-2.5 space-y-2">
      <Label htmlFor={`comment-${stage}`} className="text-[11px] font-medium uppercase tracking-wide text-violet-700 dark:text-violet-300 flex items-center gap-1">
        <MessageSquare className="h-3 w-3" aria-hidden="true" />
        Reviewer comment
        {initialNote && (
          <span className="ml-auto font-mono text-[10px] normal-case text-violet-600/80 dark:text-violet-400/80">
            existing note loaded
          </span>
        )}
      </Label>
      <textarea
        id={`comment-${stage}`}
        value={note}
        onChange={(e) => { setNote(e.target.value); }}
        rows={3}
        spellCheck
        placeholder={
          gateLabel
            ? `Optional note — flows to ${gateLabel}'s review. Saving empty clears it.`
            : 'Free-text note for this stage. Saving with empty value clears the note.'
        }
        className="w-full rounded border border-input bg-background px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-violet-400 resize-y"
      />
      {gateLabel && (
        <p className="text-[10px] text-violet-600/80 dark:text-violet-400/80">
          This note appears in {gateLabel}'s review roll-up.
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() => { void handleSave(); }}
          disabled={saving || !dirty}
          className="bg-violet-600 hover:bg-violet-700 text-white gap-1.5"
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : savedAt ? (
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          ) : null}
          {saving ? 'Saving…' : savedAt ? 'Saved' : (initialNote ? 'Update comment' : 'Save comment')}
        </Button>
        {savedAt && !dirty && (
          <span className="text-xs text-muted-foreground">
            at {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        {error && (
          <span role="alert" className="text-xs text-destructive">{error}</span>
        )}
      </div>
    </div>
  );
}
